/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 */

/**
 * @author Satyaki Bose
 * syllabusReducer.js
 * Reducer function for syllabus context state management
 */

import { SYLLABUS_ACTIONS } from '../../common/constants';
import initialState from '../InitialStates/SyllabusInitialState';
import Utils from '../../common/utils';

// Reducer function
function syllabusReducer(state, action) {
  switch (action.type) {
    case SYLLABUS_ACTIONS.SET_FILE:
      return {
        ...state,
        uploadedFile: action.payload,
        uploadError: null
      };

    case SYLLABUS_ACTIONS.REMOVE_FILE:
      return {
        ...state,
        uploadedFile: null,
        uploadError: null
      };

    case SYLLABUS_ACTIONS.START_UPLOAD:
      return {
        ...state,
        isUploading: true,
        progress: 10,
        uploadError: null
      };

    case SYLLABUS_ACTIONS.SET_PROGRESS:
      return {
        ...state,
        progress: action.payload
      };

    case SYLLABUS_ACTIONS.UPLOAD_SUCCESS: {
      const shouldAppend = action.payload.isBulkEditMode && state.syllabusInfo?.data?.schedule?.length > 0;
      const incomingSchedule = action.payload.syllabusInfo?.data?.schedule || [];
      const rawSchedule = shouldAppend
        ? [...state.syllabusInfo.data.schedule, ...incomingSchedule]
        : incomingSchedule;

      // Payload schedule is already deduped from handleExternalUploadComplete; re-run only when merging
      const newSchedule = shouldAppend
        ? Utils.validateAssignmentChapters(
          rawSchedule,
          action.payload.tocData,
          action.payload.restrictAlreadyAssigned
        )
        : rawSchedule;

      return {
        ...state,
        isUploading: false,
        progress: 100,
        uploadId: action.payload.id,
        syllabusInfo: {
          ...action.payload.syllabusInfo,
          data: {
            ...action.payload.syllabusInfo?.data,
            schedule: newSchedule
          }
        },
        assignments: newSchedule,
        tabValue: action.payload.tabValue !== undefined ? action.payload.tabValue : state.tabValue
      };
    }

    case SYLLABUS_ACTIONS.UPLOAD_ERROR:
      return {
        ...state,
        isUploading: false,
        progress: 0,
        uploadError: action.payload
      };

    case SYLLABUS_ACTIONS.RESET_UPLOAD:
      return {
        ...initialState,
        tabValue: state.tabValue // Keep current tab
      };

    case SYLLABUS_ACTIONS.SET_TAB:
      return {
        ...state,
        tabValue: action.payload
      };

    case SYLLABUS_ACTIONS.SET_ASSIGNMENTS:
      return {
        ...state,
        assignments: action.payload
      };

    case SYLLABUS_ACTIONS.SET_DRAG_ACTIVE:
      return {
        ...state,
        isDragActive: action.payload
      };

    case SYLLABUS_ACTIONS.ADD_ASSIGNMENT:
      return {
        ...state,
        syllabusInfo: {
          ...state.syllabusInfo,
          data: {
            ...state.syllabusInfo?.data,
            schedule: [
              ...(state.syllabusInfo?.data?.schedule || []),
              action.payload
            ]
          }
        },
        assignments: [
          ...(state.assignments || []),
          action.payload
        ]
      };

    case SYLLABUS_ACTIONS.UPDATE_ASSIGNMENT: {
      const { index, assignment } = action.payload;
      const updatedSchedule = [...(state.syllabusInfo?.data?.schedule || [])];
      const updatedAssignments = [...(state.assignments || [])];

      const nextAssignment = {
        ...assignment,
        coverageDedupeAction: null
      };

      if (index >= 0 && index < updatedSchedule.length) {
        updatedSchedule[index] = nextAssignment;
        updatedAssignments[index] = nextAssignment;
      }

      return {
        ...state,
        syllabusInfo: {
          ...state.syllabusInfo,
          data: {
            ...state.syllabusInfo?.data,
            schedule: updatedSchedule
          }
        },
        assignments: updatedAssignments
      };
    }

    case SYLLABUS_ACTIONS.DELETE_ASSIGNMENT: {
      const { index } = action.payload;
      const updatedSchedule = [...(state.syllabusInfo?.data?.schedule || [])];
      const updatedAssignments = [...(state.assignments || [])];

      if (index >= 0 && index < updatedSchedule.length) {
        updatedSchedule.splice(index, 1);
        updatedAssignments.splice(index, 1);
      }

      return {
        ...state,
        syllabusInfo: {
          ...state.syllabusInfo,
          data: {
            ...state.syllabusInfo?.data,
            schedule: updatedSchedule
          }
        },
        assignments: updatedAssignments
      };
    }

    case SYLLABUS_ACTIONS.SET_INITIAL_SYLLABUS: {
      const rawSchedule = action.payload?.data?.schedule || [];
      const runCoverageDedupe = action.runCoverageDedupe === true;

      const schedule = runCoverageDedupe
        ? Utils.validateAssignmentChapters(
          rawSchedule,
          action.tocData,
          action.restrictAlreadyAssigned
        )
        : rawSchedule;

      return {
        ...state,
        syllabusInfo: {
          ...action.payload,
          data: {
            ...action.payload?.data,
            schedule
          }
        },
        assignments: schedule,
        // Use targetTabValue from action (respects tabOrder) or keep current tabValue
        tabValue: action.payload?.data?.schedule?.length > 0 ? action.targetTabValue : state.tabValue,
        isUploading: false,
        progress: action.payload ? 100 : 0,
        uploadId: action.payload?.id || null
      };
    }

    case SYLLABUS_ACTIONS.SET_DATE_ADJUST_PARAMS:
      return {
        ...state,
        dateAdjustParams: action.payload
      };

    case SYLLABUS_ACTIONS.BULK_UPDATE_ASSIGNMENTS:
      return {
        ...state,
        syllabusInfo: {
          ...state.syllabusInfo,
          data: {
            ...state.syllabusInfo?.data,
            schedule: action.payload
          }
        },
        assignments: action.payload
      };

    case SYLLABUS_ACTIONS.SET_LAST_ASSIGNMENT_DEFAULTS:
      return {
        ...state,
        assignmentDefaults: action.payload
      };

    default:
      return state;
  }
}

export default syllabusReducer;
